import React, { useEffect, useRef, useState } from "react";
import { Container, Grid, Typography, Box, Card, CardContent } from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import DevicesIcon from "@mui/icons-material/Devices";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { styled } from '@mui/material/styles';

// Styled components
const GradientCard = styled(Card)(({ theme, visible, index }) => ({
  height: '100%',
  transition: 'transform 0.5s ease-out, box-shadow 0.5s ease-out, opacity 0.8s ease-out',
  borderRadius: 24,
  overflow: 'hidden',
  background: `linear-gradient(135deg, #0f172a 0%, ${
    ['#4f46e5', '#7c3aed', '#2563eb', '#8b5cf6', '#3b82f6', '#6366f1'][index % 6]
  }99 100%)`,
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(50px)',
  boxShadow: visible ? '0 10px 30px rgba(0, 0, 0, 0.25)' : '0 0 0 rgba(0, 0, 0, 0)',
  '&:hover': {
    transform: 'translateY(-15px) scale(1.02)',
    boxShadow: '0 25px 35px rgba(0, 0, 0, 0.4)',
  },
}));

const IconBox = styled(Box)(({ theme, index }) => ({
  padding: theme.spacing(2),
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.3s ease, background-color 0.3s ease',
  '&:hover': {
    transform: 'rotate(10deg) scale(1.1)',
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
  },
}));

const BenefitBox = styled(Box)(({ theme, index }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  borderLeft: `4px solid ${
    ['#4f46e5', '#7c3aed', '#2563eb', '#8b5cf6', '#3b82f6', '#6366f1'][index % 6]
  }`,
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateX(8px)',
  },
}));

const GradientButton = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
  backgroundColor: 'transparent',
  background: 'linear-gradient(45deg,rgb(246, 162, 59),rgb(250, 193, 96))',
  backgroundSize: '200% auto',
  color: 'white',
  border: 'none',
  borderRadius: '50px',
  fontWeight: 600,
  fontSize: '1rem',
  cursor: 'pointer',
  transition: 'all 0.5s ease',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 10px 20px rgba(79, 70, 229, 0.3)',
  '&:hover': {
    backgroundPosition: 'right center',
    transform: 'scale(1.05)',
    boxShadow: '0 15px 30px rgba(79, 70, 229, 0.4)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
    transition: 'left 0.7s ease',
  },
  '&:hover::before': {
    left: '100%',
  },
}));

const featuresData = [
  {
    title: "Instant Payments",
    description: "Transfer money instantly to anyone, anywhere in the world with zero waiting time.",
    icon: <SpeedIcon fontSize="large" sx={{ color: "#ffffff" }} />,
    benefit: "Save time and eliminate payment delays for your business or personal transfers."
  },
  {
    title: "Bank-Grade Security",
    description: "Industry-leading encryption and security protocols protect your financial data.",
    icon: <SecurityIcon fontSize="large" sx={{ color: "#ffffff" }} />,
    benefit: "Complete peace of mind knowing your money and information are always protected."
  },
  {
    title: "Multi-Platform Support",
    description: "Access RockkPay from any device - mobile, tablet, or desktop with seamless synchronization.",
    icon: <DevicesIcon fontSize="large" sx={{ color: "#ffffff" }} />,
    benefit: "Manage your finances on the go with our responsive cross-platform experience."
  },
  {
    title: "Flexible Payment Options",
    description: "Connect multiple payment methods including credit cards, bank accounts, and digital wallets.",
    icon: <PaymentIcon fontSize="large" sx={{ color: "#ffffff" }} />,
    benefit: "Enjoy maximum flexibility with how you send and receive money."
  },
  {
    title: "24/7 Customer Support",
    description: "Our dedicated support team is available around the clock to assist with any issues.",
    icon: <SupportAgentIcon fontSize="large" sx={{ color: "#ffffff" }} />,
    benefit: "Get help whenever you need it, no matter the time or day."
  },
  {
    title: "Smart Money Management",
    description: "Built-in tools to track expenses, set budgets, and analyze your financial patterns.",
    icon: <AccountBalanceWalletIcon fontSize="large" sx={{ color: "#ffffff" }} />,
    benefit: "Make smarter financial decisions with comprehensive insights into your spending."
  }
];

const FeaturesRockpay = () => {
  const [visible, setVisible] = useState(Array(featuresData.length).fill(false));
  const [titleVisible, setTitleVisible] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);
  const cardRefs = useRef([]);
  const titleRef = useRef(null);
  const buttonRef = useRef(null);

  // Intersection Observer setup for scroll animations
  useEffect(() => {
    // Observer for feature cards
    const cardObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = cardRefs.current.findIndex(ref => ref === entry.target);
          
          // Staggered animation for cards
          setTimeout(() => {
            setVisible(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
          }, index * 150); // 150ms delay between each card
          
          cardObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    // Observer for title section
    const titleObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setTitleVisible(true);
          titleObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    // Observer for button section
    const buttonObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setButtonVisible(true);
          buttonObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    // Observe all elements
    cardRefs.current.forEach(ref => {
      if (ref) cardObserver.observe(ref);
    });
    
    if (titleRef.current) {
      titleObserver.observe(titleRef.current);
    }
    
    if (buttonRef.current) {
      buttonObserver.observe(buttonRef.current);
    }

    return () => {
      cardRefs.current.forEach(ref => {
        if (ref) cardObserver.unobserve(ref);
      });
      
      if (titleRef.current) {
        titleObserver.unobserve(titleRef.current);
      }
      
      if (buttonRef.current) {
        buttonObserver.unobserve(buttonRef.current);
      }
    };
  }, []);

  return (
    <Box
    id="Home-Section-Four"
    sx={{ 
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      py: 10,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background elements */}
      <Box sx={{
        position: 'absolute',
        top: '10%',
        right: '5%',
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, rgba(99,102,241,0) 70%)',
        zIndex: 0,
        animation: 'pulse 15s infinite alternate',
        '@keyframes pulse': {
          '0%': { opacity: 0.4, transform: 'scale(1)' },
          '100%': { opacity: 0.7, transform: 'scale(1.2)' }
        }
      }} />
      
      <Box sx={{
        position: 'absolute',
        bottom: '15%',
        left: '10%',
        width: 250,
        height: 250,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, rgba(124,58,237,0) 70%)',
        zIndex: 0,
        animation: 'float 20s infinite alternate',
        '@keyframes float': {
          '0%': { transform: 'translateY(0) scale(1)' },
          '100%': { transform: 'translateY(30px) scale(1.1)' }
        }
      }} />

      <Container sx={{ position: 'relative', zIndex: 1 }}>
        <Box 
          ref={titleRef}
          sx={{ 
            textAlign: "center", 
            mb: 8,
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease'
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: "linear-gradient(90deg, #4f46e5 0%, #8b5cf6 50%, #6366f1 100%)",
              backgroundClip: "text",
              textFillColor: "transparent",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: '0 5px 15px rgba(99, 102, 241, 0.3)'
            }}
          >
            Powerful Features, Real Benefits
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              maxWidth: "800px", 
              mx: "auto",
              color: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            Discover how RockkPay transforms the way you handle payments and manage your finances
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {featuresData.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box 
                ref={el => cardRefs.current[index] = el}
                sx={{ height: '100%' }}
              >
                <GradientCard 
                  visible={visible[index]} 
                  index={index}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                      <IconBox index={index}>
                        {item.icon}
                      </IconBox>
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{ 
                        fontWeight: 600, 
                        mb: 2, 
                        textAlign: "center",
                        color: '#ffffff'
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mb: 3,
                        color: 'rgba(255, 255, 255, 0.8)'
                      }}
                    >
                      {item.description}
                    </Typography>
                    <BenefitBox index={index}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'rgba(255, 255, 255, 0.9)'
                        }}
                      >
                        <strong>Key Benefit:</strong> {item.benefit}
                      </Typography>
                    </BenefitBox>
                  </CardContent>
                </GradientCard>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box 
          ref={buttonRef}
          sx={{ 
            textAlign: "center", 
            mt: 8,
            opacity: buttonVisible ? 1 : 0,
            transform: buttonVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
            transitionDelay: '0.3s'
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Join thousands of satisfied users who have revolutionized their payment experience with RockkPay.
          </Typography>
          <GradientButton component="button">
            Get Started Now
          </GradientButton>
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesRockpay;